(function () {
  window.Home = window.Home || {};

  const DEFAULT_PAGE_SIZE = 100;

  /**
   * The RecommendedTasks class is used to manage the paging context for data
   * from the POP service. We use a class so that multiple instances of it can
   * be created for different lists.
   *
   * @param {String} filter Filter type: 'all', 'temple', 'record'
   * @returns {void}
   */
  function RecommendedTasks(filter) {

    // We group data from the POP service by personId and save them here.
    this.personsWithTasks = [];

    // This is the list of streamed tasks before being transformed and grouped by personId
    this.streamedTasks = [];

    // An index into the personsWithTasks array representing the next element
    // that hasn't been requested by the list which consumes this service.
    this.currentPersonIndex = 0;

    // Number of tasks request for each call to the POP service
    this.pageSize = DEFAULT_PAGE_SIZE;
    this.page = 1;
    this.noMoreTasks = false;
    this.serverNoMore = false;
    this.filter = filter || 'all';
  }

  /**
   * Get and return the given number of tasks, if we have that many available.
   *
   * @param {number} numToSend the number of tasks to get
   * @returns {object} a tasks object
   */
  RecommendedTasks.prototype.getTasks = function (numToSend) {
    let tasksToSend = [];

    // If we don't have numToSend tasks then send what we can, if we can
    if (this.personsWithTasks.length < numToSend + this.currentPersonIndex) {
      numToSend = this.personsWithTasks.length - this.currentPersonIndex;
      if (numToSend < 0) {
        numToSend = 0;
      }
    }
    if (numToSend === 0) {
      tasksToSend = [];
    } else {
      tasksToSend = this.personsWithTasks.slice(this.currentPersonIndex, this.currentPersonIndex + numToSend);
    }

    // Since personsWithTasks.slice's start parameter is inclusive, currentPersonIndex is the index of the nextTask we'll send
    this.currentPersonIndex += numToSend;

    if (FS.showEx('forceEmptyStateTasksEx')) {
      tasksToSend = [];
    }

    return tasksToSend;
  };

  /**
   * Get more data from POP, if there is more.
   *
   * @returns {void}
   */
  RecommendedTasks.prototype.getNextTasks = async function () {

    // Just return nothing if we know there's no more to get
    if (this.serverNoMore) {
      return;
    }

    // Check the temple pref. If they're members but have the
    // temple pref turned off then we don't want to request
    // temple tasks. By default the 'all' unfiltered list includes
    // temple tasks for members so we add the record filter to
    // exclude the temple tasks.
    // It's not so important to check for non-members because
    // the backend won't return temple data for them, even
    // if we ask for it.
    const ordinanceAccess = await FS.User.shouldSeeTempleInfo();
    const offset = (this.page - 1) * this.pageSize;
    let filter;
    if ((this.filter === 'all' && !ordinanceAccess) || this.filter === 'record') {
      filter = 'RECORD';
    } else if (this.filter === 'temple') {
      filter = 'TEMPLE';
    } else {
      filter = await calculateDefaultFilter();
    }
    return RecommendedTasks.getTasksFromPOP(this.pageSize, offset, filter);
  };

  /**
   * Retrieve more data from the POP service then filter out unwanted tasks and
   * reduce into a list that is grouped by PID.
   *
   * @returns {Promise<void>} an empty promise
   */
  RecommendedTasks.prototype.getNextBatchFromServer = async function () {

    // Get more tasks from the server
    const taskData = await this.getNextTasks();
    this.page++;
    if (!taskData || !taskData.hasMore) {
      this.serverNoMore = true;
    }
    if (taskData) {
      const tasks = taskData.formattedOpportunities || [];
      // If, by chance, all tasks received back were filtered out, resolve without getting TF data.
      // Otherwise, continue to build person object by adding personal data from TF.
      if (tasks.length > 0) {
        const newTasks = await requestAndMergeTreeDataWithTasks(tasks)
        this.personsWithTasks = this.personsWithTasks.concat(newTasks);
      }
    }
  };

  /**
   * Get next batch of recommended tasks. Here we decided whether we need to
   * request more data from the POP service.
   *
   * @param {number} numToGet number of tasks to get
   * @returns {object} an object of tasks
   */
  RecommendedTasks.prototype.getBatch = async function (numToGet) {
    if (numToGet === null || numToGet === undefined) {
      numToGet = this.pageSize;
    }

    // We use a promise to treat both a synchronous and an async task as async.
    const serverPromise = new Promise(async (resolve, reject) => {
      // Get more data from the server if we don't have enough data
      if (this.currentPersonIndex + numToGet > this.personsWithTasks.length) {
        const value = await this.getNextBatchFromServer();
        return resolve(value);
      }
      resolve();
    });

    await serverPromise;
    const tasks = await this.getTasks(numToGet);
    // Before returning the data we calculate whether we have any more left.
    // Consuming clients can then check the noMoreTasks flag if they want to.
    this.noMoreTasks = this.serverNoMore && this.currentPersonIndex >= this.personsWithTasks.length;
    return tasks;
  };

  /**
   * This is used for updating the add a name slot count. Mainly, it is used for dismissing it. Still uses alert service, unfortunately.
   *
   * @param {object} task the task to be updated
   * @param {string} status the status of the task
   * @param {function} cb callback function
   * @returns {void}
   */
  function updateAlertTask(task, status, cb) {
    const data = task.alert;
    data.status = status ? status : "NEW";
    // alert service needs string
    data.context = JSON.stringify(data.context);
    Home.xhr.put('/service/home/alerts/alert/' + data.id, data).then(xmlhttp => {
      if (xmlhttp.status === 200) {
        if (cb) cb(xmlhttp.responseData);
      }
    });
  }

  /**
   * Dismisses a recommended task from POP
   *
   * @param {object} task task to be dismissed
   * @returns {void}
   */
  function dismissTask(task) {
    toggleDismissTask(task, true)
  }

  /**
   * Undismisses a recommended task from POP
   *
   * @param {object} task task to be undismissed
   * @returns {void}
   */
  function undoDismissTask(task) {
    toggleDismissTask(task, false)
  }

  /**
   * Undismisses a recommended task from POP
   *
   * @param {object} task task to be either dismissed or undismissed
   * @param {boolean} shouldDismiss true/false should we dismiss this task
   * @returns {void}
   */
  function toggleDismissTask(task, shouldDismiss) {
    let url = `/service/home/pop/users/${FS.User.getEffectiveId()}/opportunities?personId=${task.personId}&resourceUri=${task.resourceUri}&type=${task.type}`
    if (shouldDismiss) {
      url += "&status=DISMISSED";
    }
    Home.xhr.patch(url).catch(err => {
      //This error is not handled because the data has already been deleted from the database
      console.error(err);
    });
  }

  /**
   * Get temple ordinance data from Temple Icon Cache to populate the green
   * temple dropdown ordinance squares and give the reason why an orange temple
   * (in its dropdown) is not ready.
   *
   * @param {string} pid personId of the
   * @returns {void}
   */
  function getTempleOrdinances(pid) {
    // Note: eventually this header will not be needed and will become the default behavior, but it's needed for now
    headers = {};
    if (FS.showEx('templeSharedGreenEx')) {
      headers = {'X-FS-Feature-Tag': 'reservationsSharedReadyEx'};
    }
    return Home.xhr.get('/service/tree/tree-data/reservations/person/' + pid + '/card', headers).then(xmlhttp => {
      if (xmlhttp.status === 200) {
        return xmlhttp.responseData;
      }
      throw new Error("Get Temple Ordinances Network Call Error:", xmlhttp.status);
    }).catch(e => {
      throw new Error("Get Temple Ordinances Network Call Error: " + e.status);
    });
  }

  /**
   * Refresh opportunity data for a given PID.
   *
   * @param {string} pid personId to refresh
   * @param {string} oppType type of opportunity to refresh
   * @returns {void}
   */
  function refreshOpportunities(pid, oppType) {
    return Home.xhr.post('/service/home/pie/events', {
      type: 'REFRESH',
      body: {
        personIds: [pid],
        opportunityType: oppType
      }
    }).catch(err => {
      //This function is part of the error handling of another function
      console.error(err);
    });
  }

  /**
   * Request tasks from POP
   *
   * @param {number} pageSize max number of tasks to get
   * @param {number} offset for paginating
   * @param {string} filter limit to a single type of task
   * @returns {object} the tasks retrieved from POP
   */
  async function getTasksFromPOP(pageSize, offset, filter = '') {
    const segment = await FS.User.getSegmentData();
    if (segment && segment.treeLevel === 'EMPTY') {
      this.noMoreTasks = true;
      this.serverNoMore = true;
      return;
    }

    const defaultFilter = await calculateDefaultFilter();
    const unfilteredFirstPage = offset === 0 && filter === defaultFilter;

    // If the first page of the unfiltered list has already been
    // requested then return its promise.
    if (unfilteredFirstPage && Home.RecommendedTasks.unfilteredFirstPagePromise) {
      return Home.RecommendedTasks.unfilteredFirstPagePromise;
    }
    // used to prevent cached results when a person or activity is removed
    let url = `/service/home/pop/users/${FS.User.getEffectiveId()}/opportunities/summaries?includeVisited=false&limit=${pageSize}&offset=${offset}`;

    if (filter) {
      url += `&type=${filter.toUpperCase()}`;
    }
    if (FS.showEx('beginnerClosenessParamRTEx') && (getIsNewUserOverride() || segment.newUser) ) { //override is for testing
      url += `&sortBy=CLOSENESS`;
    }

    const headers = {
      'pragma': 'no-cache',
      'cache-control': 'no-cache'
    }
    const dataPromise = Home.xhr.get(url, headers).then(data => {
      if (data.responseData && data.responseData.personSummaries) { // if false, most likely a 204 was returned.
        data.responseData.formattedOpportunities = data.responseData.personSummaries.map(formatDataMap,{filter}).filter((person)=> person.opportunities.length > 0 );
        return data.responseData;
      } //2nd return isn't needed because the code checks for `formattedOpportunities` before continuing 
    }).catch(err => {
      //This error rarely happens and triggers an infinite spinner so we're ignoring it for now
      console.error(err);
    });

    // If this is the first page of the unfiltered list then save it's
    // promise so that it can be reused later. It can be requested up
    // to three times: one for first page of the unfiltered list and
    // two more times for the hasTasksOfType() checks that enable the
    // filter buttons.
    if (unfilteredFirstPage) {
      Home.RecommendedTasks.unfilteredFirstPagePromise = dataPromise;
    }

    return dataPromise;
  }
  
  /**
   * Checks if there is a query param override. This check matches the query param used in fsh-card-feed
   *
   * @returns {boolean} if there is a query param override it returns a boolean, otherwise it returns undefined
   */
  function getIsNewUserOverride(){
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('feedType')) {
      return urlParams.get('feedType').toUpperCase().includes('NEW');
    }
  }

  /**
   * Filter for living people and people who don't have a name
   *
   * @param {object} treeData Response data from batch tf summary call
   * @param {object} personWithTasks Task data
   * @returns {boolean} `true` if the person is not living and has a name, else `false`
   */
  function filterPeople(treeData, personWithTasks) {
    if (treeData[personWithTasks.personId].status === "RESTRICTED") return false;
    if (!treeData[personWithTasks.personId].personCardSummary) return false;
    const personTFData = treeData[personWithTasks.personId].personCardSummary.summary;
    // Adding hasNoName variable to filter out persons who don't have a name listed
    const hasNoName = personTFData.name === "" || personTFData.name === "?";
    return (!personTFData.living && !hasNoName);
  }

  /**
   * Gets the tree data and combines it with the ancestors we have tasks for
   *
   * @param {array} newPersonsWithTasks all of the people we have tasks for
   * @returns {Promise} promise with the updated people with tree data
   */
  function requestAndMergeTreeDataWithTasks(newPersonsWithTasks) {
    // Assemble the query params in the form: ?id=PID1&id=PID2
    const url = "/service/tree/tf/person/summary?featureTag=GenericRelationshipTerms&id=" + newPersonsWithTasks.map(personWithTasks => {
      return personWithTasks.personId;
    }).join('&id=');
    return Home.xhr.get(url).then(xmlhttp => {
      if (xmlhttp.status === 200) {
        const resData = xmlhttp.responseData;
        return newPersonsWithTasks
          .filter(filterPeople.bind(null, resData))
          .map(personWithTasks => {
            const summaryData = resData[personWithTasks.personId].personCardSummary.summary;
            personWithTasks.name = summaryData.name;
            personWithTasks.lifespan = summaryData.lifespan;
            personWithTasks.sex = summaryData.gender;
            return personWithTasks;
          });
      }
    }).catch(err => {
      //This error rarely happens
      console.error(err);
    });
  }

  /**
   * Calculate whether the user has any tasks for the given type.
   * Used to decide whether to enable the filter buttons.
   *
   * First we check the first page of the All list. It's an optimization
   * that allows us to only make 1 request to POP on the initial page load
   * instead of 3 (one for each tab). If we don't find any  in the first
   * page then we request the first full page of the given type. This
   * accounts for the case where the first page of the All list only
   * has one type of task and other types don't appear until later pages.
   *
   * @param {String} taskType Task type
   * @returns {Boolean} Whether the user has any tasks of the given type.
   */
  async function hasTasksOfType(taskType) {
    try {
      await FS.User.shouldSeeTempleInfo();
      const defaultFilter = await calculateDefaultFilter();
      const firstPageTasks = await RecommendedTasks.getTasksFromPOP(DEFAULT_PAGE_SIZE, 0, defaultFilter);

      // Return false if we have no tasks
      if (!firstPageTasks || !firstPageTasks.formattedOpportunities) {
        return false;
      }

      const allTasks = firstPageTasks.formattedOpportunities;

      // Return false if we have no filtered tasks
      if (!allTasks.length) {
        return false;
      }

      // Return true if there are tasks of the given type
      for (let i = 0; i < allTasks.length; i++) {
        if ( (taskType === "TEMPLE" && allTasks[i].hasTemple)
          || (taskType === "RECORD" && allTasks[i].hasRecord) ) {
          return true;
        }
      }

      // If no tasks match then fetch a list of the given type. This
      // accounts for the case where the user has many tasks and no tasks of
      // the given type are in the first page of results.
      return serverHasTasksOfType(taskType);

    } catch (error) {
      console.error('Error getting tasks:', error);
      return false;
    }
  }

  /**
   * Check whether the user has any tasks of the given type by loading
   * the first page of that task type.
   *
   * @param {String} taskType Task type to check for
   * @returns {Boolean} Whether the user has any tasks of the given type.
   */
  async function serverHasTasksOfType(taskType) {
    const typeTasks = await RecommendedTasks.getTasksFromPOP(DEFAULT_PAGE_SIZE, 0, taskType);
    if (typeTasks && typeTasks.formattedOpportunities) {
      const tasks = typeTasks.formattedOpportunities;
      if (tasks.length) {
        return true;
      }
    }
    return false;
  }

  /**
   * Gets the default filter (records or temple or all) for a user
   *
   * @returns {string} filter
   */
  async function calculateDefaultFilter() {
    const ordinanceAccess = await FS.User.shouldSeeTempleInfo();
    return ordinanceAccess ? '' : 'RECORD';
  }

  /**
   * This is used to format the data from POP to meet the needs to the application
   *
   * @param {object} originalPerson the original person as received from POP
   * @returns {object} formated person
   */
  function formatDataMap(originalPerson){
    const person = {
      personId: originalPerson.personId,
      id: originalPerson.personId, //duplicate field because some of the depedancies expect "id" instead of "personId". Keeping "personId" because it's more descriptive.
      opportunities: [],
      types: []
    }
    if ( this.filter !== "RECORD" 
      && originalPerson.hasTemple
      && originalPerson.templeType !== "DUPLICATE_PERSON"
      && originalPerson.templeType !== "NEED_PERMISSION" ) {
      
      person.hasTemple = true;
      const templeOpportunity = {
        type: "TEMPLE",
        personId: originalPerson.personId,
        resourceUri: originalPerson.personId //for a temple opportunity the personId & resourceUri are the same
      }
      person.opportunities.push(templeOpportunity)
      // Converting RESERVED_SHARED_READY to READY since the behavior is the same.  This avoids having to handle RESERVED_SHARED_READY everywhere else in the code.
      if (originalPerson.templeType === 'RESERVED_SHARED_READY') {
        originalPerson.templeType = 'READY';
      }
      if (originalPerson.templeType === 'READY') {
        person.types.push('green')
      } else if (originalPerson.templeType === "NEED_MORE_INFORMATION") {
        person.types.push('yellow')
      }
    }
    if (this.filter !== "TEMPLE" && originalPerson.hasRecord) {
      person.hasRecord = true;
      const records = [
        ...originalPerson.recordTypes.OTHER,
        ...originalPerson.recordTypes.NEW_SOURCE,
        ...originalPerson.recordTypes.NEW_PERSON,
        ...originalPerson.recordTypes.NEW_CONCLUSION
      ]
      .filter((item, index, array)=>array.indexOf(item) === index) //deduping
      .map((record)=>{
        return {
          type: "RECORD", 
          personId: originalPerson.personId,
          resourceUri: record
        }
      });
      person.opportunities = [...person.opportunities, ...records]
    }
    return person;
  }

  Home.RecommendedTasks = RecommendedTasks;

  // Setup static methods
  RecommendedTasks.updateAlertTask = updateAlertTask;
  RecommendedTasks.dismissTask = dismissTask;
  RecommendedTasks.undoDismissTask = undoDismissTask;
  RecommendedTasks.getTempleOrdinances = getTempleOrdinances;
  RecommendedTasks.hasTasksOfType = hasTasksOfType;
  RecommendedTasks.refreshOpportunities = refreshOpportunities;
  RecommendedTasks.getTasksFromPOP = getTasksFromPOP;

}());
